import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import { Button, Input, Select, Modal, Toast } from '../components/UI';
import { questionsAPI, youthAPI } from '../services/api';

// ===== Constants =====
const CATEGORIES = [
  { value: 'ishsizlik', label: 'Ishsizlik', icon: 'briefcase', color: '#ef4444' },
  { value: 'talim', label: "Ta'lim", icon: 'graduation', color: '#3b82f6' },
  { value: 'uy_joy', label: 'Uy-joy', icon: 'home', color: '#f59e0b' },
  { value: 'infratuzilma', label: 'Infratuzilma', icon: 'building', color: '#8b5cf6' },
  { value: 'sogliq', label: "Sog'liq", icon: 'heart', color: '#10b981' },
  { value: 'moliyaviy', label: 'Moliyaviy', icon: 'trend', color: '#6366f1' },
  { value: 'tadbirkorlik', label: 'Tadbirkorlik', icon: 'store', color: '#ea580c' },
  { value: 'huquqiy', label: 'Huquqiy', icon: 'shield', color: '#0891b2' },
  { value: 'ijtimoiy', label: 'Ijtimoiy', icon: 'users', color: '#14b8a6' },
  { value: 'boshqa', label: 'Boshqa', icon: 'flag', color: '#64748b' },
];

const STATUSES = [
  { value: 'yuborildi', label: 'Yuborildi', color: '#6366f1', bg: '#eef2ff', icon: 'plane' },
  { value: 'oqildi', label: "O'qildi", color: '#f59e0b', bg: '#fffbeb', icon: 'eye' },
  { value: 'javob_berildi', label: 'Javob berildi', color: '#22c55e', bg: '#f0fdf4', icon: 'checkCircle' },
  { value: 'rad_etildi', label: 'Rad etildi', color: '#ef4444', bg: '#fef2f2', icon: 'x' },
];

const getCat = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[9];
const getSt = (v) => STATUSES.find(s => s.value === v) || STATUSES[0];

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

// ===== Main Page =====
export default function HokimQuestionsPage() {
  const user = getUser();
  const isAdmin = user?.role === 'viloyat_admin' || user?.role === 'tuman_admin';

  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [statusF, setStatusF] = useState('');
  const [categoryF, setCategoryF] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF) params.status = statusF;
      if (categoryF) params.category = categoryF;
      if (search) params.search = search;
      if (selectedRegion) {
        if (user?.role === 'viloyat_admin') params.tuman = selectedRegion;
        else params.mfy = selectedRegion;
      }
      const [qRes, sRes] = await Promise.all([
        questionsAPI.list(params),
        questionsAPI.stats(),
      ]);
      setQuestions(qRes.data);
      setStats(sRes.data);
    } catch { showToast("Yuklanmadi", 'error'); }
    setLoading(false);
  }, [statusF, categoryF, search, selectedRegion]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    try {
      await questionsAPI.create(data);
      showToast("Savol yuborildi!");
      setFormOpen(false);
      load();
    } catch { showToast("Xatolik", 'error'); }
  };

  const handleDelete = async () => {
    try {
      await questionsAPI.delete(deleteId);
      showToast("Savol o'chirildi");
      setDeleteId(null);
      load();
    } catch { showToast("Xatolik", 'error'); }
  };

  const loadAndView = async (id) => {
    try { const r = await questionsAPI.get(id); setViewItem(r.data); } catch {}
  };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* ===== HERO CARD ===== */}
      <div style={{
        background: 'linear-gradient(135deg, #312e81 0%, #4338ca 40%, #6366f1 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: -20, right: 60, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ width: 46, height: 46, borderRadius: 14, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="messageSquare" size={24} color="#fff" />
              </div>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0 }}>Hokimga savol yo'llash</h2>
                <p style={{ fontSize: 13, color: '#c7d2fe', margin: 0 }}>Tuman yoki viloyat hokimiga murojaat qiling</p>
              </div>
            </div>
          </div>
          {!isAdmin && <Button onClick={() => setFormOpen(true)} icon="plus"
            style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.3)', color: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            Savol yo'llash
          </Button>}
        </div>

        {/* Mini stats */}
        <div style={{ display: 'flex', gap: 20, marginTop: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Jami', value: stats.total || 0, icon: 'messageSquare' },
            { label: 'Yuborildi', value: stats.yuborildi || 0, icon: 'plane' },
            { label: "O'qildi", value: stats.oqildi || 0, icon: 'eye' },
            { label: 'Javob berildi', value: stats.javob_berildi || 0, icon: 'checkCircle' },
            { label: 'Shoshilinch', value: stats.urgent || 0, icon: 'zap' },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name={s.icon} size={15} color="#c7d2fe" />
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: '#a5b4fc', fontWeight: 500 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ADMIN: jadval yoki tanlangan hudud savollari */}
      {isAdmin && !selectedRegion && (stats.tuman_breakdown || stats.mfy_breakdown) ? (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🏢</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
              {user?.role === 'viloyat_admin' ? 'Tumanlar' : 'MFYlar'} bo'yicha savollar hisoboti
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
              Savollarni ko'rish uchun hududni tanlang
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 750 }}>
              <thead>
                <tr style={{ background: '#fafbff' }}>
                  {['#', user?.role === 'viloyat_admin' ? 'Tuman' : 'MFY', 'Jami', 'Yuborildi', "O'qildi", 'Javob berildi', 'Rad etildi', 'Shoshilinch'].map(h => (
                    <th key={h} style={{ padding: '12px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '2px solid #e2e8f0', textAlign: h === '#' || h === 'Tuman' || h === 'MFY' ? 'left' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(stats.tuman_breakdown || stats.mfy_breakdown || []).map((row, i) => {
                  const rKey = user?.role === 'viloyat_admin' ? 'tuman' : 'mfy';
                  const name = row[rKey] || "Noma'lum";
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                      onClick={() => setSelectedRegion(name)}
                      onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '11px 10px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '11px 10px', fontSize: 13, fontWeight: 600, color: '#4338ca' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {name} <Icon name="chevronRight" size={14} color="#a5b4fc" />
                        </span>
                      </td>
                      <td style={{ padding: '11px 10px', textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{row.jami}</td>
                      <td style={{ textAlign: 'center' }}>{row.yuborildi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#6366f1' }}>{row.yuborildi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.oqildi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#ca8a04' }}>{row.oqildi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.javob_berildi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#16a34a' }}>{row.javob_berildi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.rad_etildi_c > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fef2f2', color: '#dc2626' }}>{row.rad_etildi_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.shoshilinch_c > 0 ? <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13 }}>{row.shoshilinch_c}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                  <td style={{ padding: '12px 10px' }} />
                  <td style={{ padding: '12px 10px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>JAMI</td>
                  {(() => {
                    const bd = stats.tuman_breakdown || stats.mfy_breakdown || [];
                    const sum = (k) => bd.reduce((s, r) => s + (r[k] || 0), 0);
                    return (
                      <>
                        <td style={{ textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{sum('jami')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#6366f1' }}>{sum('yuborildi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#ca8a04' }}>{sum('oqildi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{sum('javob_berildi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{sum('rad_etildi_c')}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{sum('shoshilinch_c')}</td>
                      </>
                    );
                  })()}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <>
          {/* Orqaga tugma (admin hudud tanlagan) */}
          {isAdmin && selectedRegion && (
            <div style={{ marginBottom: 16 }}>
              <button onClick={() => setSelectedRegion(null)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12,
                background: '#eef2ff', border: '1.5px solid #c7d2fe', color: '#4338ca',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <Icon name="arrowLeft" size={16} /> Hisobotga qaytish
              </button>
              <span style={{ marginLeft: 12, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                📍 {selectedRegion} — savollar
              </span>
            </div>
          )}

          {/* STATUS TABS */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
            <TabBtn active={statusF === ''} onClick={() => setStatusF('')} icon="messageSquare" label="Barchasi" count={questions.length} />
            {STATUSES.map(s => (
              <TabBtn key={s.value} active={statusF === s.value} onClick={() => setStatusF(s.value)}
                icon={s.icon} label={s.label} color={s.color} bg={s.bg}
                count={s.value === 'yuborildi' ? stats.yuborildi : s.value === 'oqildi' ? stats.oqildi : s.value === 'javob_berildi' ? stats.javob_berildi : stats.rad_etildi || 0} />
            ))}
          </div>

          {/* FILTERS */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 320 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..."
                style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }} />
              <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}><Icon name="search" size={15} /></div>
            </div>
            <select value={categoryF} onChange={e => setCategoryF(e.target.value)}
              style={{ padding: '11px 14px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)' }}>
              <option value="">Barcha toifa</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* QUESTION LIST */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-dim)' }}>Yuklanmoqda...</div>
          ) : questions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 20, border: '2px dashed var(--border)' }}>
              <Icon name="messageSquare" size={48} color="#cbd5e1" />
              <p style={{ fontSize: 15, color: '#94a3b8', marginTop: 12 }}>Savollar topilmadi</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {questions.map(q => (
                <QuestionCard key={q.id} question={q}
                  onView={() => loadAndView(q.id)}
                  onDelete={() => setDeleteId(q.id)} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== MODALS ===== */}
      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="Hokimga savol yo'llash" width={640}>
        <QuestionForm onSave={handleCreate} onClose={() => setFormOpen(false)} />
      </Modal>

      <Modal open={!!viewItem} onClose={() => setViewItem(null)} title="Savol tafsilotlari" width={640}>
        {viewItem && <QuestionDetail question={viewItem} />}
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="O'chirishni tasdiqlang">
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>Ushbu savolni o'chirishni xohlaysizmi?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>Bekor qilish</Button>
          <Button variant="danger" onClick={handleDelete}>O'chirish</Button>
        </div>
      </Modal>
    </div>
  );
}

// ===== Tab Button =====
function TabBtn({ active, onClick, icon, label, count, color, bg }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 12,
      border: active ? `2px solid ${color || 'var(--accent)'}` : '2px solid var(--border)',
      background: active ? (bg || 'var(--accent-light)') : '#fff',
      color: active ? (color || 'var(--accent)') : 'var(--text-muted)',
      fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'all .2s',
    }}>
      <Icon name={icon} size={16} /> {label}
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: active ? (color || 'var(--accent)') : '#e2e8f0', color: active ? '#fff' : 'var(--text-dim)' }}>{count}</span>
    </button>
  );
}

// ===== Empty State =====
function EmptyState({ onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 20, border: '2px dashed var(--border)' }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <Icon name="messageSquare" size={32} color="var(--accent)" />
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Hali savol yo'llanmagan</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>Tuman yoki viloyat hokimiga birinchi savolingizni yo'llang</p>
      <Button onClick={onAdd} icon="plus">Savol yo'llash</Button>
    </div>
  );
}

// ===== Question Card =====
function QuestionCard({ question: q, onView, onDelete }) {
  const cat = getCat(q.category);
  const st = getSt(q.status);
  const isAnswered = q.status === 'javob_berildi';
  const isRejected = q.status === 'rad_etildi';
  const isSent = q.status === 'yuborildi';

  const timeDiff = () => {
    const now = new Date();
    const created = new Date(q.created_at);
    const mins = Math.floor((now - created) / 60000);
    if (mins < 60) return `${mins} daqiqa oldin`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} soat oldin`;
    const days = Math.floor(hours / 24);
    return `${days} kun oldin`;
  };

  return (
    <div onClick={onView} style={{
      background: '#fff', borderRadius: 16, padding: '20px 24px', cursor: 'pointer',
      border: `1.5px solid ${isAnswered ? '#bbf7d0' : isRejected ? '#fecaca' : 'var(--border)'}`,
      borderLeft: `4px solid ${st.color}`,
      transition: 'all .2s', position: 'relative',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(99,102,241,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>

      {/* Urgent badge */}
      {q.is_urgent && (
        <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: '#fef2f2', color: '#ef4444', fontSize: 11, fontWeight: 700 }}>
          <Icon name="zap" size={12} /> Shoshilinch
        </div>
      )}

      {/* Row 1: Addressee + Category + Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: q.addressee === 'viloyat_hokim' ? '#fdf4ff' : '#eef2ff', color: q.addressee === 'viloyat_hokim' ? '#a855f7' : '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {q.addressee === 'viloyat_hokim' ? '🏛 Viloyat hokimi' : '🏢 Tuman hokimi'}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: cat.color + '12', color: cat.color }}>
          <Icon name={cat.icon} size={11} /> {cat.label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20, background: st.bg, color: st.color }}>
          <Icon name={st.icon} size={12} /> {st.label}
        </span>
      </div>

      {/* Title */}
      <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6, paddingRight: q.is_urgent ? 100 : 0 }}>{q.title}</h3>

      {/* Youth + MFY info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
        {q.youth_name && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="users" size={12} /> {q.youth_name}
          </span>
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon name="clock" size={12} /> {timeDiff()}
        </span>
      </div>

      {/* Answer preview */}
      {isAnswered && q.answered_at && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13, color: '#166534', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="checkCircle" size={16} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontWeight: 600 }}>Javob berilgan — {q.answered_at?.slice(0, 10)}</span>
        </div>
      )}
      {isRejected && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', fontSize: 13, color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="x" size={16} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontWeight: 600 }}>Rad etilgan — {q.answered_at?.slice(0, 10)}</span>
        </div>
      )}

      {/* Bottom: Time + Delete */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          {q.created_at?.slice(0, 10)}
        </span>
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          {isSent && (
            <button onClick={onDelete} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--red)' }}>
              <Icon name="trash" size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Question Form =====
function QuestionForm({ onSave, onClose }) {
  const [youthList, setYouthList] = useState([]);
  const [form, setForm] = useState({
    title: '',
    body: '',
    category: 'ishsizlik',
    addressee: 'tuman_hokim',
    youth: '',
    is_urgent: false,
  });

  useEffect(() => {
    youthAPI.mini().then(r => setYouthList(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = () => {
    if (!form.title || !form.body) return;
    const data = { ...form };
    if (!data.youth) delete data.youth;
    onSave(data);
  };

  return (
    <div>
      {/* Addressee selection */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Kimga yuborilsin? <span style={{ color: 'var(--red)' }}>*</span></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { value: 'tuman_hokim', label: 'Tuman hokimi', icon: '🏢', desc: 'Tuman miqyosidagi masalalar' },
            { value: 'viloyat_hokim', label: 'Viloyat hokimi', icon: '🏛', desc: 'Viloyat miqyosidagi masalalar' },
          ].map(a => (
            <button key={a.value} onClick={() => set('addressee', a.value)} style={{
              padding: '16px 18px', borderRadius: 14, textAlign: 'left', fontFamily: 'inherit',
              border: form.addressee === a.value ? '2.5px solid var(--accent)' : '2px solid var(--border)',
              background: form.addressee === a.value ? 'var(--accent-light)' : '#fff',
              cursor: 'pointer', transition: 'all .2s',
            }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{a.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: form.addressee === a.value ? 'var(--accent-dark)' : 'var(--text)' }}>{a.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{a.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Category chips */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Toifa <span style={{ color: 'var(--red)' }}>*</span></label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {CATEGORIES.map(c => (
            <button key={c.value} onClick={() => set('category', c.value)} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20,
              border: form.category === c.value ? `2px solid ${c.color}` : '2px solid var(--border)',
              background: form.category === c.value ? c.color + '12' : '#fff',
              color: form.category === c.value ? c.color : 'var(--text-muted)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <Icon name={c.icon} size={13} /> {c.label}
            </button>
          ))}
        </div>
      </div>

      <Input label="Savol sarlavhasi" value={form.title} onChange={v => set('title', v)} required placeholder="Qisqa va aniq sarlavha..." />

      {/* Body */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Savol matni <span style={{ color: 'var(--red)' }}>*</span></label>
        <textarea value={form.body} onChange={e => set('body', e.target.value)} rows={5}
          placeholder="Muammo yoki savolingizni batafsil yozing. Raqamlar va faktlar bilan asoslang..."
          style={{ width: '100%', padding: '14px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6 }} />
      </div>

      {/* Youth link (optional) */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Bog'liq yosh (ixtiyoriy)</label>
        <select value={form.youth} onChange={e => set('youth', e.target.value)}
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit' }}>
          <option value="">Tanlash shart emas</option>
          {youthList.map(y => <option key={y.id} value={y.id}>{y.full_name} ({y.age} yosh)</option>)}
        </select>
      </div>

      {/* Urgent toggle */}
      <div onClick={() => set('is_urgent', !form.is_urgent)} style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 12, cursor: 'pointer', marginBottom: 20,
        border: form.is_urgent ? '2px solid #ef4444' : '2px solid var(--border)',
        background: form.is_urgent ? '#fef2f2' : '#fff',
      }}>
        <div style={{
          width: 44, height: 24, borderRadius: 12, padding: 2,
          background: form.is_urgent ? '#ef4444' : '#e2e8f0', transition: 'all .2s',
          display: 'flex', alignItems: 'center',
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'all .2s',
            transform: form.is_urgent ? 'translateX(20px)' : 'translateX(0)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          }} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: form.is_urgent ? '#ef4444' : 'var(--text)' }}>
            ⚡ Shoshilinch savol
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            Tezkor ko'rib chiqilishi kerak bo'lsa belgilang
          </div>
        </div>
      </div>

      {/* Submit */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onClose}>Bekor qilish</Button>
        <Button onClick={handleSubmit} disabled={!form.title || !form.body} icon="plane">
          Savolni yuborish
        </Button>
      </div>
    </div>
  );
}

// ===== Question Detail =====
function QuestionDetail({ question: q }) {
  const cat = getCat(q.category);
  const st = getSt(q.status);

  return (
    <div>
      {/* Badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 20, background: q.addressee === 'viloyat_hokim' ? '#fdf4ff' : '#eef2ff', color: q.addressee === 'viloyat_hokim' ? '#a855f7' : '#6366f1' }}>
          {q.addressee === 'viloyat_hokim' ? '🏛 Viloyat hokimi' : '🏢 Tuman hokimi'}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: cat.color + '12', color: cat.color }}>
          <Icon name={cat.icon} size={14} /> {cat.label}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: st.bg, color: st.color }}>
          <Icon name={st.icon} size={14} /> {st.label}
        </span>
        {q.is_urgent && (
          <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 20, background: '#fef2f2', color: '#ef4444' }}>
            ⚡ Shoshilinch
          </span>
        )}
      </div>

      {/* Title */}
      <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{q.title}</h3>
      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span><Icon name="clock" size={12} style={{ verticalAlign: 'middle' }} /> {q.created_at?.slice(0, 10)}</span>
        {q.youth_name && <span><Icon name="users" size={12} style={{ verticalAlign: 'middle' }} /> {q.youth_name}</span>}
        {q.created_by_name && <span>Yuboruvchi: {q.created_by_name}</span>}
      </div>

      {/* Body - chat bubble style */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="plane" size={13} /> SAVOL
        </div>
        <div style={{
          padding: '16px 20px', borderRadius: '4px 16px 16px 16px',
          background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
          border: '1px solid #e0e7ff', fontSize: 14, lineHeight: 1.7, color: 'var(--text)',
        }}>
          {q.body}
        </div>
      </div>

      {/* Answer */}
      {q.answer && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: q.status === 'rad_etildi' ? '#ef4444' : '#22c55e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name={q.status === 'rad_etildi' ? 'x' : 'checkCircle'} size={13} />
            {q.status === 'rad_etildi' ? 'RAD ETILDI' : 'JAVOB'}
            {q.answered_by_name && <span style={{ fontWeight: 400, color: 'var(--text-dim)' }}>— {q.answered_by_name}</span>}
            {q.answered_at && <span style={{ fontWeight: 400, color: 'var(--text-dim)' }}>• {q.answered_at?.slice(0, 10)}</span>}
          </div>
          <div style={{
            padding: '16px 20px', borderRadius: '16px 4px 16px 16px',
            background: q.status === 'rad_etildi' ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${q.status === 'rad_etildi' ? '#fecaca' : '#bbf7d0'}`,
            fontSize: 14, lineHeight: 1.7, color: 'var(--text)',
          }}>
            {q.answer}
          </div>
        </div>
      )}

      {/* Waiting state */}
      {!q.answer && (
        <div style={{
          padding: '20px', borderRadius: 14, background: '#fffbeb', border: '1.5px dashed #fcd34d',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>⏳</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>Javob kutilmoqda</div>
          <div style={{ fontSize: 12, color: '#a16207', marginTop: 4 }}>
            {q.status === 'oqildi' ? "Savolingiz o'qildi, javob tayyorlanmoqda" : "Savolingiz yuborildi, ko'rib chiqilishini kuting"}
          </div>
        </div>
      )}
    </div>
  );
}
